import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarBlank, Clock, X } from "@phosphor-icons/react";

const TZ = "America/Santo_Domingo";
const pad = (n) => String(n).padStart(2, "0");

export const fmtRD = (iso) => new Date(iso).toLocaleString("es-DO", {
  timeZone: TZ, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});

// RD has no DST: always UTC-4.
const toRdIso = (day, time) => `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}T${time}:00-04:00`;

const rdParts = (iso) => {
  const d = new Date(new Date(iso).getTime() - 4 * 3600 * 1000);
  return { day: new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()), time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` };
};

export function ScheduleDialog({ campaign, onClose, onDone }) {
  const nextHour = rdParts(new Date((Math.floor(Date.now() / 3600000) + (new Date().getMinutes() >= 50 ? 2 : 1)) * 3600000).toISOString());
  const init = campaign?.scheduled_at ? rdParts(campaign.scheduled_at) : nextHour;
  const [day, setDay] = useState(init.day);
  const [time, setTime] = useState(init.time);
  const [busy, setBusy] = useState(false);
  const today = rdParts(new Date().toISOString()).day;

  const save = async () => {
    if (!day || !time) return toast.error("Elige fecha y hora");
    setBusy(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.id}/schedule`, { scheduled_at: toRdIso(day, time) });
      toast.success(`Campaña programada para ${fmtRD(data.scheduled_at)} (${data.total} destinatarios)`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo programar"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!campaign} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="campaign-schedule-dialog">
        <DialogHeader>
          <DialogTitle>{campaign?.status === "scheduled" ? "Reprogramar campaña" : "Programar campaña"}</DialogTitle>
          <DialogDescription>{campaign?.name} · hora de República Dominicana</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <Calendar mode="single" selected={day} onSelect={setDay} disabled={(d) => d < today} data-testid="campaign-schedule-date" />
          <label className="flex items-center gap-2 text-sm self-start">
            <Clock size={16} /> Hora
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="w-32" data-testid="campaign-schedule-time" />
          </label>
          {day && time && <div className="text-xs text-muted-foreground self-start" data-testid="campaign-schedule-summary">Saldrá el {fmtRD(toRdIso(day, time))}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={busy} data-testid="campaign-schedule-save">{busy ? "Guardando…" : "Programar envío"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ScheduleActions({ c, onSchedule, onChanged }) {
  const unschedule = async () => {
    if (!window.confirm("¿Cancelar la programación? La campaña vuelve a borrador.")) return;
    try { await api.post(`/campaigns/${c.id}/unschedule`); toast.success("Programación cancelada"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
  };
  if (c.status === "draft") return (
    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onSchedule(c)} data-testid={`campaign-schedule-${c.id}`}>
      <CalendarBlank size={14} /> Programar
    </Button>
  );
  if (c.status !== "scheduled") return null;
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onSchedule(c)} data-testid={`campaign-reschedule-${c.id}`}>
        <CalendarBlank size={14} /> Reprogramar
      </Button>
      <Button size="sm" variant="outline" className="gap-1.5" onClick={unschedule} data-testid={`campaign-unschedule-${c.id}`}>
        <X size={14} /> Cancelar programación
      </Button>
    </>
  );
}
